import { inject, Injectable } from '@angular/core';
import { StorageService } from '@core/services/storages/storage.service';
import { defer, firstValueFrom, map, Observable, shareReplay } from 'rxjs';

const STORAGE_KEY = 'inboxImpressions';

/**
 * Which inbox messages have already had their "Inbox impression" this session.
 *
 * "Mixpanel > Inboxes": one impression per message per user session, and on
 * web the session ends at sign-out. The ids therefore live in IndexedDB —
 * `clearAppData()` on sign-out is what ends them — and not in the store, which
 * a reload empties. They used to be a store slice keyed by a `sessionId` the
 * messages widget generated for itself; the reducer never wrote the slice, and
 * the widget is created afresh every time the side menu unfolds, so every
 * opening of the pop-up tracked every visible message again (measured: 6
 * impressions for 3 messages in two openings).
 */
@Injectable({ providedIn: 'root' })
export class InboxImpressionsService {
  private readonly storage = inject(StorageService);

  // Read once, on the first message shown; the set is the source of truth
  // from then on and every change is written through. A read that fails
  // (private mode, a cleared database) starts empty: the worst case is a
  // repeated impression.
  private readonly shown$: Observable<Set<number>> = defer(() =>
    this.storage.getField<number[]>(STORAGE_KEY)
  ).pipe(
    map((ids) => new Set(Array.isArray(ids) ? ids : [])),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  /**
   * Remembers the message as shown. True the first time in this session —
   * the caller tracks the impression — and false from then on.
   */
  async markShown(id: number): Promise<boolean> {
    const shown = await firstValueFrom(this.shown$);
    if (shown.has(id)) return false;

    shown.add(id);
    try {
      await firstValueFrom(
        this.storage.upsertFields({ [STORAGE_KEY]: [...shown] })
      );
    } catch {
      console.warn(
        'Inbox impression could not be remembered — it will be tracked again after a reload'
      );
    }
    return true;
  }
}
